import {
  useQuery,
  UseQueryResult,
  UseQueryOptions,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import { buildQueryString } from "@mimber-client/utils"
import {
  MemberEventRegistrationWithAttendanceDto,
  ResponseDto,
  QueryParamsDto,
} from "@mimber/schema"
import { memberEventsKeys } from "../member-events.utils"

/**
 * Hook for fetching a member's event registrations with attendance data
 * Can only be used in mimber-organization app
 *
 * @param memberUuid - Member UUID
 * @param params - Query parameters (pagination, sorting, filtering)
 * @param options - React Query options
 */
export const useMemberEvents = (
  memberUuid: string,
  params?: QueryParamsDto,
  options?: UseQueryOptions<
    ResponseDto<MemberEventRegistrationWithAttendanceDto[]>,
    Error
  >
): UseQueryResult<
  ResponseDto<MemberEventRegistrationWithAttendanceDto[]>,
  Error
> => {
  guardAppUsage("mimber-organization", "useMemberEvents")

  const { server } = useServerContext()

  return useQuery({
    queryKey: memberEventsKeys.getMemberEvents(memberUuid, params),
    queryFn: async () => {
      const response = await server.get<
        ResponseDto<MemberEventRegistrationWithAttendanceDto[]>
      >(buildQueryString(`members/${memberUuid}/events`, params))
      return response.data
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!memberUuid,
    ...options,
  })
}
